import { useLayoutEffect, useState } from 'react';

/**
 * Vertical positioning for the mega menu panel. When the panel becomes visible,
 * finds the nearest header ancestor and returns its bottom edge (in px) so the
 * panel can be anchored flush beneath the header via `top`/`maxHeight`.
 *
 * Two selectors are checked, in priority order, because `MegaMenu` can mount
 * under either of two headers depending on how the widget is running:
 *   - `.drawer-header` is CMS-template scaffolding markup that wraps this widget
 *     on a live dealer-site page (see the "CMS scaffolding rule" comment in
 *     `src/global-sass/mixins.scss`) — present when the widget is injected into
 *     an existing CMS header.
 *   - `.ws-nav-drawer__header-row` is this widget's own header row (rendered in
 *     `NavigationDrawer.jsx`) — present when the widget runs as a full drawer
 *     replacement.
 * The `.ws-nav-drawer__header-row` fallback is not dead code: it is the header
 * that exists when there is no surrounding CMS scaffolding.
 *
 * @param {React.RefObject<HTMLElement>} menuRef - Ref to the panel root.
 * @param {boolean} visible - Whether the panel is currently shown; positioning
 *   only recomputes while visible.
 * @returns {number} topPx - Bottom edge of the resolved header, or the last
 *   computed value (0 before the first successful measurement).
 */
const useMegaMenuPosition = (menuRef, visible) => {
	const [topPx, setTopPx] = useState(0);

	useLayoutEffect(() => {
		if (!visible) return;
		const el = menuRef.current;
		if (!el) return;
		const header =
			el.closest('.drawer-header') ||
			el.closest('.ws-nav-drawer__header-row');
		if (header) {
			setTopPx(header.getBoundingClientRect().bottom);
		}
	}, [visible, menuRef]);

	return topPx;
};

export default useMegaMenuPosition;
